import { useContext, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader, Page } from '../../components';
import { UserContext } from '../../context';
import { DynamicFieldAPI } from '../../fetch/dynamic-fields';
import { IResponse } from '../../models';
import { IDynamicField } from '../../models/dynamic-fields';
import { ILookupType } from '../../models/employee';
import { Add, Delete, Edit, Warning } from '@mui/icons-material';
import { Box, Button, IconButton, Theme } from '@mui/material';
import { DraggableTable } from '../../components/fullTable/DraggableTable';
import { DynamicFieldsFilters } from './DynamicFieldsFilters';
import { useToastContext } from '../../hooks';
import { MobileDraggableTable } from '../../components/fullTable/MobileDraggableTable';
import { DynamicFieldsModal } from './DynamicFieldsModal';
import { ConfirmationDialogue } from '../../components/modal/ConfirmationDialogue';
import { theme } from '../../styles';
import { getNonLocalDateTime } from '../../helpers';
export const DynamicFields = () => {
  const { showToast } = useToastContext();
  const { userContext } = useContext(UserContext);

  // Modal
  const [selectedDynamicFieldId, setselectedDynamicFieldId] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const handleModalOpen = () => {
    setIsModalOpen(true);
  };
  const handleModalClose = () => {
    setIsModalOpen(false);
    setselectedDynamicFieldId('');
  };

  useEffect(() => {
    if (selectedDynamicFieldId) {
      handleModalOpen();
    }
  }, [selectedDynamicFieldId]);

  // Delete Confirmation
  const [dynamicFieldToDelete, setDynamicFieldToDelete] = useState<IDynamicField | null>(null);

  const { isLoading: isLoadingEntityCount, data: EntityCount } = useQuery<number, Error>({
    queryKey: ['entityCount', dynamicFieldToDelete?.dynamicFieldId],
    queryFn: async () => {
      const data = await DynamicFieldAPI.fetchEntityCount(
        dynamicFieldToDelete?.dynamicFieldId ?? ('' as string)
      );
      return data;
    },
    onError(error: any) {
      showToast('error', error?.detail || 'Error trying to load entity count. Please try again.');
    },
    enabled: !!dynamicFieldToDelete,
  });

  const [selectedTeamId, setSelectedTeamId] = useState<string | number>('');
  const [selectedEntityTypeId, setSelectedEntityTypeId] = useState<string | number>('');
  const [sortString, setSortString] = useState<string>('');

  const [filters, setFilters] = useState<{
    teamId?: string | number;
    entityTypeId?: string | number;
  }>({ teamId: selectedTeamId, entityTypeId: selectedEntityTypeId });

  const { isLoading: isLoadingTeams, data: teams } = useQuery<ILookupType[], Error>({
    queryKey: ['teams'],
    queryFn: async () => {
      const data = await DynamicFieldAPI.fetchTeam(userContext?.employeeId as string);
      return data;
    },
    onError(error: any) {
      showToast('error', error?.detail || 'Error trying to load teams. Please try again.');
    },
    enabled: !!userContext?.employeeId,
  });

  const { isLoading: isLoadingEntityTypes, data: entityTypes } = useQuery<ILookupType[], Error>({
    queryKey: ['entityTypes'],
    queryFn: async () => {
      const data = await DynamicFieldAPI.fetchEntityTypes();
      return data;
    },
    onError(error: any) {
      showToast('error', error?.detail || 'Error trying to load entity types. Please try again.');
    },
  });

  useEffect(() => {
    if (teams?.length) {
      setSelectedTeamId(teams[0].value);
    }
    if (entityTypes?.length) {
      setSelectedEntityTypeId(entityTypes[0].value);
    }
  }, [teams, entityTypes]);

  const [dynamicFieldsData, setDynamicFieldsData] = useState<IDynamicField[]>([]);
  const {
    isLoading: isLoadingDynamicFieldsData,
    isRefetching,
    refetch: refetchDynamicFieldsData,
  } = useQuery<IResponse<IDynamicField[]>, Error>({
    queryKey: [
      'DynamicFields',
      filters,
      //@ts-ignore
      sortString,
    ],
    queryFn: async () => {
      const data = await DynamicFieldAPI.getAll({
        teamId: selectedTeamId,
        entityTypeId: selectedEntityTypeId,
        sortBy: !!sortString.split('-')?.[0] ? sortString.split('-')?.[0] : undefined,
        //@ts-ignore
        sortDirection: !!sortString.split('-')?.[1] ? sortString.split('-')?.[1] : undefined,
      });

      return data;
    },

    // Only run when you have selectedTeamId and selectedEntityTypeId
    enabled: !!selectedTeamId && !!selectedEntityTypeId,
    onSuccess: data => {
      setDynamicFieldsData(data.records.map(record => ({ ...record, id: record.dynamicFieldId })));
    },
  });

  const handleClickColumn = (column: string) => {
    const sortStringArray = sortString.split('-');
    const previousSortColumn = sortStringArray?.[0];
    const previousSortDirection = sortStringArray?.[1];

    if (column === previousSortColumn) {
      if (previousSortDirection === 'Asc') {
        setSortString(`${column}-Desc`);
      } else {
        setSortString('');
      }
    } else {
      setSortString(`${column}-Asc`);
    }
  };

  const columns = useMemo(() => {
    return [
      {
        accessor: 'entityTypeName',
        Header: 'Type',
        sort: false,
      },
      {
        accessor: 'name',
        Header: 'Name',
        isServerSorted: sortString.split('-')?.[0] === 'name',
        isServerSortedDesc: sortString.split('-')?.[1] === 'Desc',
        handleClickColumn: () => handleClickColumn('name'),
      },
      {
        accessor: 'dataTypeName',
        Header: 'Input Type',
        sort: false,
      },
      {
        accessor: 'teamName',
        Header: 'Team',
        isServerSorted: sortString.split('-')?.[0] === 'teamName',
        isServerSortedDesc: sortString.split('-')?.[1] === 'Desc',
        handleClickColumn: () => handleClickColumn('teamName'),
      },
      {
        accessor: 'lastModifiedBy',
        Header: 'Last Modified By',
        isServerSorted: sortString.split('-')?.[0] === 'lastModifiedBy',
        isServerSortedDesc: sortString.split('-')?.[1] === 'Desc',
        handleClickColumn: () => handleClickColumn('lastModifiedBy'),
        Cell: ({
          cell: {
            row: { original },
          },
        }: {
          cell: { row: { original: any } };
        }) => {
          return (
            <div>
              {original.lastModifiedBy ? original.lastModifiedBy : ''}{' '}
              {original?.lastModifiedDateTimeStamp
                ? getNonLocalDateTime(original?.lastModifiedDateTimeStamp)
                : ''}
            </div>
          );
        },
      },

      {
        accessor: 'actions',
        Header: () => {
          return (
            <Box
              display="flex"
              justifyContent="end"
              sx={{
                '& > *:not(:last-child)': {
                  marginRight: theme => theme.spacing(1),
                },
                '.MuiButton-root.Mui-disabled': {
                  border: '1px solid rgba(140, 155, 147, 0.5)',
                  color: 'rgb(255 255 255 / 26%)',
                  backgroundColor: '#19667E',
                },
              }}
            >
              {/** ADD, SAVE BUTTONS */}
              {
                <Button
                  sx={(theme: Theme) => ({
                    color: '#fff',
                    borderColor: 'rgb(255 255 255 / 50%)',
                    '&:hover': {
                      color: theme.frankie.searchBlue,
                      borderColor: '#ffffff',
                      backgroundColor: '#ffffff',
                    },
                  })}
                  startIcon={<Add />}
                  variant="outlined"
                  onClick={() => setselectedDynamicFieldId('new')}
                >
                  Add
                </Button>
              }
            </Box>
          );
        },
        sort: false,
        Cell: ({
          cell: {
            row: { original },
          },
        }: {
          cell: { row: { original: any } };
        }) => {
          return (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <IconButton
                onClick={() => {
                  setselectedDynamicFieldId(original.dynamicFieldId);
                }}
              >
                <Edit />
              </IconButton>
              <IconButton onClick={() => setDynamicFieldToDelete(original)}>
                <Delete />
              </IconButton>
            </Box>
          );
        },
      },
    ];
    // @ts-ignore
    //  eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dynamicFieldsData, sortString, filters]);

  useEffect(() => {
    if (sortString !== '') {
      setDisableDnD(true);
    }
    if (dynamicFieldsData.length > 0 && sortString === '') {
      setDisableDnD(false);
      try {
        DynamicFieldAPI.updateListOrder({
          dynamicFieldIds: dynamicFieldsData.map(item => item.dynamicFieldId),
        });
      } catch (error: any) {
        showToast(
          'error',
          error?.detail || 'Error trying to update dynamic field list. Please try again.'
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dynamicFieldsData, sortString]);

  const [disabledDnD, setDisableDnD] = useState(false);
  const handleSort = (e: any, column: any) => {};

  const [hasAppliedFilters, setHasAppliedFilters] = useState<boolean>(false);

  return (
    <>
      <Page title="Manage Dynamic Fields">
        <DynamicFieldsFilters
          isLoading={
            isLoadingDynamicFieldsData || isLoadingTeams || isLoadingEntityTypes || isRefetching
          }
          applyFilters={(clearFilters?: boolean) => {
            if (clearFilters) {
              setFilters({
                teamId: teams?.[0].value ?? '',
                entityTypeId: entityTypes?.[0].value ?? '',
              });
              setSortString('');
            } else {
              setFilters({
                ...filters,
                teamId: selectedTeamId ?? '',
                entityTypeId: selectedEntityTypeId ?? '',
              });
              setSortString('');
            }
          }}
          types={entityTypes ?? []}
          selectedType={selectedEntityTypeId}
          setSelectedType={setSelectedEntityTypeId}
          teams={teams ?? []}
          selectedTeam={selectedTeamId}
          setSelectedTeam={setSelectedTeamId}
          hasAppliedFilters={hasAppliedFilters}
          setHasAppliedFilters={setHasAppliedFilters}
        />
        <DraggableTable
          isLoading={
            isLoadingDynamicFieldsData || isLoadingTeams || isLoadingEntityTypes || isRefetching
          }
          columns={columns}
          data={dynamicFieldsData}
          setData={setDynamicFieldsData}
          handleSort={handleSort}
          disabledDnD={disabledDnD}
          mobileProps={{
            fields: columns
              .filter(col => col.Header)
              .filter(col => col.accessor !== 'actions')
              .map(col => ({
                name: col.Header,
                accessor: col.accessor,
                Cell: col?.Cell,
              })),
            mobileAccordionLabel: (original: any) => `${original?.name ?? ''}`,
          }}
          ResponsiveComponent={MobileDraggableTable}
        />
        <DynamicFieldsModal
          open={isModalOpen}
          onClose={handleModalClose}
          selectedDynamicFieldId={selectedDynamicFieldId}
          employeeId={userContext?.employeeId}
          selectedTypeId={selectedEntityTypeId}
          selectedType={
            entityTypes?.find(type => type.value === selectedEntityTypeId)?.description ?? ''
          }
          selectedTeamId={selectedTeamId}
          selectedTeam={teams?.find(team => team.value === selectedTeamId)?.description ?? ''}
          refetchDynamicFieldsData={refetchDynamicFieldsData}
        />
        <ConfirmationDialogue
          open={!!dynamicFieldToDelete}
          id="delete-dynamic-field"
          title={`Delete Custom Field - ${dynamicFieldToDelete?.name}`}
          text={''}
          customContent={
            <>
              <div>If deleted, you will lose this data permanently!</div>
              <div>
                <span style={{ fontWeight: 600 }}>Warning</span>
              </div>
              <div>
                The selected custom field has live data assigned for{' '}
                {typeof EntityCount === 'number' && !isLoadingEntityCount ? (
                  EntityCount
                ) : isLoadingEntityCount ? (
                  <Loader type={'inline'} />
                ) : (
                  '(?)'
                )}{' '}
                different records.
              </div>
              <div
                style={{ color: theme.palette.error.main, display: 'flex', alignItems: 'center' }}
              >
                <Warning
                  style={{
                    marginRight: theme.spacing(0.5),
                  }}
                />
                Are you sure you want to delete this custom field for your entire team?
              </div>
            </>
          }
          onClose={() => setDynamicFieldToDelete(null)}
          onConfirm={async () => {
            // Hit the API and delete
            try {
              await DynamicFieldAPI.deleteDynamicField(dynamicFieldToDelete?.dynamicFieldId ?? '');
              refetchDynamicFieldsData();
            } catch (error: any) {
              const detailedError = { error };
              const message =
                detailedError?.error || 'Error trying to delete dynamic field. Please try again.';
              showToast('error', message);
            } finally {
              setDynamicFieldToDelete(null);
            }
          }}
        />
      </Page>
    </>
  );
};
